/**
 * Chart.js plugin: draw group labels (e.g. Body Mass Index, Blood Pressure) in the left
 * margin of the Y-axis, rotated 90° to save space, and horizontal divider lines between groups.
 * Expects chart.options.plugins.consolidationGroups = [ { category, startIndex, endIndex } ].
 */
const GROUP_LABEL_LEFT_PADDING = 24;
const GROUP_LABEL_FONT_SIZE = 6;
const GROUP_LABEL_FONT = 'OpenSans';
const GROUP_LABEL_MAX_CHARS_PER_LINE = 7;
const GROUP_LABEL_LINE_HEIGHT = 7;
const DIVIDER_COLOR = '#999';
const DIVIDER_LINE_WIDTH = 0.5;

/** Wrap long category text into multiple lines to avoid overlap when rotated. */
function wrapCategoryText(text, maxChars = GROUP_LABEL_MAX_CHARS_PER_LINE) {
  if (!text || text.length <= maxChars) return [text].filter(Boolean);
  const words = text.split(/\s+/);
  const lines = [];
  let current = '';
  words.forEach((w) => {
    if (w.length > maxChars) {
      if (current) {
        lines.push(current);
        current = '';
      }
      const chunks = w.match(new RegExp(`(.{1,${maxChars}})`, 'g')) || [w];
      lines.push(...chunks);
      return;
    }
    const next = current ? `${current} ${w}` : w;
    if (next.length <= maxChars) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = w;
    }
  });
  if (current) lines.push(current);
  return lines;
}

export function buildGroupRanges(chartData) {
  const ranges = [];
  let index = 0;
  (chartData || []).forEach((cat) => {
    const count = (cat.items || []).length;
    if (count > 0) {
      ranges.push({
        category: cat.category || '',
        startIndex: index,
        endIndex: index + count - 1,
      });
      index += count;
    }
  });
  return ranges;
}

export const consolidationGroupLabelsPlugin = {
  id: 'consolidationGroupLabels',
  afterDraw(chart) {
    const groups = chart.options.plugins?.consolidationGroups;
    if (!groups?.length || !chart.scales?.y) return;

    const groupLabelFontSize =
      chart.options.plugins?.consolidationGroupLabelFontSize ??
      GROUP_LABEL_FONT_SIZE;
    const groupLabelLineHeight =
      chart.options.plugins?.consolidationGroupLabelLineHeight ??
      Math.max(GROUP_LABEL_LINE_HEIGHT, groupLabelFontSize + 1);

    const ctx = chart.ctx;
    const yScale = chart.scales.y;
    const chartArea = chart.chartArea;
    if (!chartArea) return;

    const left = GROUP_LABEL_LEFT_PADDING;

    ctx.save();

    // 1) Horizontal divider lines between groups (from left padding to chart right)
    ctx.strokeStyle = DIVIDER_COLOR;
    ctx.lineWidth = DIVIDER_LINE_WIDTH;
    for (let i = 0; i < groups.length - 1; i++) {
      const bottom = yScale.getPixelForValue(groups[i].endIndex);
      const nextTop = yScale.getPixelForValue(groups[i + 1].startIndex);
      const lineY = (bottom + nextTop) / 2;
      ctx.beginPath();
      ctx.moveTo(0, lineY);
      ctx.lineTo(chartArea.right, lineY);
      ctx.stroke();
    }

    // 2) Group labels rotated 90° counter-clockwise (read bottom-to-top)
    ctx.font = `${groupLabelFontSize}px ${GROUP_LABEL_FONT}`;
    ctx.fillStyle = '#666';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    groups.forEach((group) => {
      const top = yScale.getPixelForValue(group.startIndex);
      const bottom = yScale.getPixelForValue(group.endIndex);
      const midY = (top + bottom) / 2;
      if (group.category) {
        const lines = wrapCategoryText(group.category);
        const totalHeight = (lines.length - 1) * groupLabelLineHeight;
        const startY = totalHeight / 2;
        ctx.save();
        ctx.translate(left, midY);
        ctx.rotate(-Math.PI / 2);
        const drawnOrder = [...lines].reverse();
        drawnOrder.forEach((line, i) => {
          ctx.fillText(line, 0, startY - i * groupLabelLineHeight);
        });
        ctx.restore();
      }
    });

    ctx.restore();
  },
};

export default consolidationGroupLabelsPlugin;
